import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Calendar, CircleDot, ChartPie, Beaker, Droplets, Zap, HeartPulse, Brain, Moon, ActivitySquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { PeriodTrackingForm } from "@/components/period-tracking/form/PeriodTrackingForm";
import { PhaseInfo } from "@/components/period-tracking/info/PhaseInfo";
import { NextPeriodInfo } from "@/components/period-tracking/info/NextPeriodInfo";
import { CycleStats } from "@/components/period-tracking/stats/CycleStats";
import CycleChart from "@/components/period-tracking/CycleChart";
import { HormoneInfo } from "@/components/period-tracking/HormoneInfo";
import { calculateNextPeriod, calculatePhase } from "@/components/period-tracking/utils/cycleCalculations";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DailyTipsButton } from "@/components/daily-tips/DailyTipsButton";

const MobilePeriodTracking = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [lastPeriodDate, setLastPeriodDate] = useState<Date | undefined>();
  const [periodDuration, setPeriodDuration] = useState("");
  const [cycleLength, setCycleLength] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  const { data: periodData, refetch, isLoading } = useQuery({
    queryKey: ["period-tracking"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from("period_tracking")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching period data:', error);
        throw error;
      }
      
      return data;
    },
  });

  useEffect(() => {
    if (periodData) {
      if (periodData.last_period_date) {
        const date = new Date(periodData.last_period_date);
        if (!isNaN(date.getTime())) {
          setLastPeriodDate(date);
          setPeriodDuration(String(periodData.period_duration || ""));
          setCycleLength(String(periodData.cycle_length || ""));
        } else {
          console.error('Invalid date format:', periodData.last_period_date);
        }
      }
    }
  }, [periodData]);

  const phase = lastPeriodDate && cycleLength && periodDuration 
    ? calculatePhase(lastPeriodDate, cycleLength, periodDuration)
    : null;
    
  const nextPeriod = lastPeriodDate && cycleLength 
    ? calculateNextPeriod(lastPeriodDate, cycleLength)
    : null;

  const currentCycleDay = phase ? 
    (phase.phase === "MENSTRUATION" ? 1 : 
     phase.phase === "INFERTILE DAYS" && phase.color === "#8DD4E6" ? 7 :
     phase.phase === "FERTILE DAYS" ? 14 : 21) : 1;

  const cycleData = {
    lastPeriodDate,
    periodDuration,
    cycleLength,
    currentPhase: phase?.phase,
    nextPeriod,
    currentCycleDay
  };

  const waves = Array.from({ length: 6 }).map((_, i) => ({
    id: i,
    height: 2 + i % 3,
    top: `${15 + i * 12}%`,
    delay: i * 0.3,
    opacity: 0.1 + (i % 3) * 0.05
  }));

  if (isLoading) {
    return (
      <div className="p-4 flex items-center justify-center h-screen">
        <div className="animate-pulse text-primary">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-20 tracking-page-content relative">
      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-pink-50 to-purple-50">
        {waves.map(wave => (
          <div 
            key={wave.id}
            className="absolute w-full"
            style={{
              top: wave.top,
              height: `${wave.height}px`,
              background: 'rgba(219, 39, 119, 0.1)',
              animation: `waveMotion ${3 + wave.id * 0.5}s infinite ease-in-out`,
              animationDelay: `${wave.delay}s`,
              opacity: wave.opacity
            }}
          />
        ))}
        
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-pink-100 rounded-full opacity-30" />
        <div className="absolute top-40 left-10 w-32 h-32 bg-purple-100 rounded-full opacity-20" />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-bold flex items-center">
          Suivi du cycle menstruel
          <Calendar className="h-5 w-5 ml-2 text-pink-500 animate-pulse" />
        </h1>
      </div>

      {phase && (
        <div className="mb-4 animate-fade-in">
          <div className="bg-white/70 backdrop-blur-sm rounded-lg overflow-hidden shadow-sm">
            <div className="p-3 relative">
              <div className="flex items-center gap-2">
                {phase.phase === "MENSTRUATION" && (
                  <div className="bg-red-400 text-white p-2 rounded-full">
                    <Droplets className="h-5 w-5" />
                  </div>
                )}
                {phase.phase === "FERTILE DAYS" && (
                  <div className="bg-green-400 text-white p-2 rounded-full">
                    <Zap className="h-5 w-5" />
                  </div>
                )}
                {phase.phase === "INFERTILE DAYS" && (
                  <div className="bg-blue-400 text-white p-2 rounded-full">
                    <Moon className="h-5 w-5" />
                  </div>
                )}
                {phase.phase === "PRE-MENSTRUAL" && (
                  <div className="bg-purple-400 text-white p-2 rounded-full">
                    <ActivitySquare className="h-5 w-5" />
                  </div>
                )}
                
                <div>
                  <h3 className="font-medium">{phase.phase}</h3>
                  <p className="text-xs text-muted-foreground">{phase.description}</p>
                </div>
              </div>
            </div>
            <div 
              className="h-2"
              style={{ background: phase.color }}
            />
          </div>
        </div>
      )}

      <div className="mobile-tabs border-b mb-4">
        <div className="flex overflow-x-auto pb-2">
          <Button 
            variant={activeTab === "overview" ? "default" : "ghost"} 
            className={`mr-2 mobile-button flex-shrink-0 ${activeTab === "overview" ? "bg-pink-500 hover:bg-pink-600" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            <HeartPulse className="h-4 w-4 mr-1" />
            Vue d'ensemble
          </Button>
          <Button 
            variant={activeTab === "form" ? "default" : "ghost"} 
            className={`mr-2 mobile-button flex-shrink-0 ${activeTab === "form" ? "bg-pink-500 hover:bg-pink-600" : ""}`}
            onClick={() => setActiveTab("form")}
          >
            <Calendar className="h-4 w-4 mr-1" />
            Informations
          </Button>
          <Button 
            variant={activeTab === "chart" ? "default" : "ghost"} 
            className={`mr-2 mobile-button flex-shrink-0 ${activeTab === "chart" ? "bg-pink-500 hover:bg-pink-600" : ""}`}
            onClick={() => setActiveTab("chart")}
          >
            <ChartPie className="h-4 w-4 mr-1" />
            Cycle
          </Button>
          <Button 
            variant={activeTab === "hormones" ? "default" : "ghost"} 
            className={`mobile-button flex-shrink-0 ${activeTab === "hormones" ? "bg-pink-500 hover:bg-pink-600" : ""}`}
            onClick={() => setActiveTab("hormones")}
          >
            <Beaker className="h-4 w-4 mr-1" />
            Hormones
          </Button>
        </div>
      </div>

      {lastPeriodDate && cycleLength && periodDuration && (
        <div className="flex justify-center my-4">
          <DailyTipsButton 
            dataToAnalyze={cycleData} 
            context="cycle"
          />
        </div>
      )}

      <ScrollArea className="h-[calc(100vh-230px)] pr-4">
        <div className="space-y-6">
          {activeTab === "overview" && (
            <>
              {lastPeriodDate && cycleLength && periodDuration && (
                <div className="glass-morphism rounded-lg p-4 animate-fade-in">
                  <CycleStats 
                    lastPeriodDate={lastPeriodDate} 
                    cycleLength={cycleLength} 
                    periodDuration={periodDuration} 
                  />
                </div>
              )}
              {phase && (
                <div className="glass-morphism rounded-lg p-4 animate-fade-in" style={{ animationDelay: "0.1s" }}>
                  <PhaseInfo phase={phase} />
                </div>
              )}
              {nextPeriod && (
                <div className="glass-morphism rounded-lg p-4 animate-fade-in" style={{ animationDelay: "0.2s" }}>
                  <NextPeriodInfo nextPeriod={nextPeriod} />
                </div>
              )}
            </>
          )}

          {activeTab === "form" && (
            <div className="glass-morphism rounded-lg p-4 animate-fade-in">
              <PeriodTrackingForm 
                periodData={periodData}
                onSuccess={refetch}
                initialData={{
                  lastPeriodDate,
                  periodDuration,
                  cycleLength
                }}
              />
            </div>
          )}

          {activeTab === "chart" && lastPeriodDate && cycleLength && (
            <div className="glass-morphism rounded-lg p-4 animate-fade-in cycle-chart-mobile">
              <h3 className="font-semibold mb-2 flex items-center">
                <CircleDot className="h-5 w-5 mr-2 text-pink-500" />
                Cycle Menstruel
              </h3>
              <div className="w-full max-w-md mx-auto">
                <CycleChart 
                  lastPeriodDate={lastPeriodDate}
                  cycleLength={cycleLength}
                />
              </div>
            </div>
          )}

          {activeTab === "hormones" && (
            <div className="glass-morphism rounded-lg p-4 animate-fade-in">
              <h3 className="font-semibold mb-2 flex items-center">
                <Brain className="h-5 w-5 mr-2 text-pink-500" />
                Impact Hormonal
              </h3>
              <HormoneInfo />
            </div>
          )}
          
          {!lastPeriodDate && activeTab !== "form" && (
            <div className="text-center py-4 glass-morphism rounded-lg p-4 animate-fade-in">
              <p className="text-muted-foreground mb-4">
                Veuillez d'abord entrer les informations de votre cycle
              </p>
              <Button 
                onClick={() => setActiveTab("form")}
                className="bg-pink-500 hover:bg-pink-600"
              >
                Entrer mes informations
              </Button>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default MobilePeriodTracking;
